import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wrench, Plus, Trash2, X, GripVertical, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Settings ▸ Maintenance visit types.
 *
 * A single admin/manager-editable list of visit types that projects can be
 * booked against. Exactly one type may be marked as the annual-reminder
 * source — that's the type used when the background reminder loop auto-
 * queues a Draft visit 30 days before the anchor's next due date.
 */
export default function MaintenanceVisitTypesSection() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/maintenance/visit-types");
      setRows(data || []);
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load visit types");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const annualSourceKey = useMemo(
    () => (rows.find(r => r.is_annual_reminder_source && r.active !== false) || {}).key,
    [rows]
  );

  const upd = (idx, k, v) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [k]: v } : r));
    setDirty(true);
  };

  const setAnnualSource = (idx) => {
    setRows(rs => rs.map((r, i) => ({ ...r, is_annual_reminder_source: i === idx })));
    setDirty(true);
  };

  const add = () => {
    const k = window.prompt("Unique key (lowercase, underscores). e.g. quarterly_north");
    if (!k) return;
    const slug = k.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (rows.some(r => r.key === slug)) { toast.error("Key already exists"); return; }
    setRows(rs => [...rs, { key: slug, label: slug.replace(/_/g, " "), cadence_months: 0,
                             description: "", is_annual_reminder_source: false, active: true }]);
    setDirty(true);
  };

  const remove = (idx) => {
    if (!window.confirm(`Remove "${rows[idx].label}"?`)) return;
    setRows(rs => rs.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const move = (from, to) => {
    if (from === to || to < 0 || to >= rows.length) return;
    setRows(rs => {
      const next = [...rs];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/maintenance/visit-types", rows);
      toast.success("Visit types saved");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (!canManage) return null;

  return (
    <section className="swiss-card-flat p-6 mb-6" data-testid="visit-types-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wrench size={14} style={{ color: "var(--accent-hover)" }} />
            <div className="label">Maintenance visit types</div>
          </div>
          <p className="muted text-xs">
            The dropdown users pick from when booking a visit. Mark exactly one type as the
            annual-reminder source — that&apos;s what the auto-scheduler queues 30 days ahead.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={add} data-testid="visit-type-add"><Plus size={13} /> Add type</button>
          <button className="btn-accent" onClick={save} disabled={!dirty || saving} data-testid="visit-type-save">
            {saving ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
            Save changes
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center muted text-sm"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading…</div>
      ) : (
        <>
          <div className="border border-[var(--line)] rounded-md overflow-hidden">
            <table className="data-table w-full" data-testid="visit-types-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Key</th>
                  <th>Label</th>
                  <th className="text-right">Cadence (mo)</th>
                  <th className="text-center">Annual source</th>
                  <th className="text-center">Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key} draggable
                      onDragStart={() => setDragIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
                      data-testid={`visit-type-row-${r.key}`}>
                    <td className="cursor-grab muted"><GripVertical size={13} /></td>
                    <td className="font-mono text-xs muted">{r.key}</td>
                    <td>
                      <input className="input-swiss py-1 text-sm" value={r.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`visit-type-label-${r.key}`} />
                    </td>
                    <td className="text-right">
                      <input type="number" min="0" className="input-swiss py-1 text-sm w-20 text-right" value={r.cadence_months || 0} onChange={(e) => upd(i, "cadence_months", Number(e.target.value))} data-testid={`visit-type-cadence-${r.key}`} />
                    </td>
                    <td className="text-center">
                      <input type="radio" name="annualSrc" checked={!!r.is_annual_reminder_source}
                             onChange={() => setAnnualSource(i)} data-testid={`visit-type-annual-${r.key}`} />
                    </td>
                    <td className="text-center">
                      <input type="checkbox" checked={r.active !== false} onChange={(e) => upd(i, "active", e.target.checked)} data-testid={`visit-type-active-${r.key}`} />
                    </td>
                    <td className="num">
                      <button onClick={() => remove(i)} data-testid={`visit-type-del-${r.key}`}>
                        <Trash2 size={13} className="muted" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] muted mt-2">
            Annual reminder source: <span className="font-mono">{annualSourceKey || "none"}</span>. Drag rows to reorder.
          </div>
        </>
      )}
    </section>
  );
}
